import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ESDM_DOMAINS, esdmDomainKey, getItemsByLevelAndDomain } from '@aba-denver/core';
import type { EsdmDomainCode, EsdmItemResult, EsdmLevel } from '@aba-denver/core';
import { useData } from '../contexts/DataContext';
import { Badge } from '../components/ui/Badge';

const SCORE_META: { key: EsdmItemResult; label: string; color: string }[] = [
  { key: 'A', label: 'Adquirido', color: '#3f9d6b' },
  { key: 'PA', label: 'Parcial', color: '#d59a37' },
  { key: 'NA', label: 'Não adquirido', color: '#cc5f42' },
  { key: 'SA', label: 'Sem avaliação', color: '#8a9095' },
];

const RESULT_BADGE: Record<EsdmItemResult, { label: string; variant: 'success' | 'warning' | 'danger' | 'neutral' }> = {
  A: { label: 'Adquirido', variant: 'success' },
  PA: { label: 'Parcial', variant: 'warning' },
  NA: { label: 'Não adquirido', variant: 'danger' },
  SA: { label: 'Sem avaliação', variant: 'neutral' },
};

export function EsdmDomain() {
  const { id, level: levelParam, domain: domainParam } = useParams();
  const navigate = useNavigate();
  const {
    getChildById,
    getChildEsdmProgress,
    canEdit,
    setEsdmItemResult,
    clearEsdmItemResult,
    setEsdmDomainSynthesis,
  } = useData();

  const child = id ? getChildById(id) : undefined;
  const level = Number(levelParam) as EsdmLevel;
  const domain = domainParam as EsdmDomainCode;

  const progressDoc = child ? getChildEsdmProgress(child.id) : undefined;
  const synthesisKey = esdmDomainKey(level, domain);
  const savedSynthesis = progressDoc?.domainSynthesis?.[synthesisKey]?.text ?? '';
  const [synthesis, setSynthesis] = useState(savedSynthesis);

  useEffect(() => {
    setSynthesis(savedSynthesis);
  }, [synthesisKey, savedSynthesis]);

  if (!child) {
    return <div className="empty-state">Paciente não encontrado.</div>;
  }

  const editable = canEdit(child);
  const domainInfo = ESDM_DOMAINS.find((d) => d.code === domain);
  const items = getItemsByLevelAndDomain(level, domain).sort((a, b) => a.number - b.number);

  const handleSaveSynthesis = () => {
    if (!editable || synthesis === savedSynthesis) return;
    void setEsdmDomainSynthesis(child.id, level, domain, synthesis);
  };

  const handleScore = (itemId: string, result: EsdmItemResult, current: EsdmItemResult | undefined) => {
    if (!editable) return;
    if (current === result) void clearEsdmItemResult(child.id, itemId);
    else void setEsdmItemResult(child.id, itemId, result);
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <a onClick={() => navigate(`/patients/${child.id}/esdm/${level}`)} style={{ cursor: 'pointer', fontSize: 13 }}>
            ‹ Nível {level}
          </a>
          <h1 style={{ marginTop: 6 }}>{domainInfo?.name ?? domain}</h1>
        </div>
      </div>

      <div className="stack">
        {items.map((item) => {
          const progress = progressDoc?.items[item.id];
          const result = progress?.result;
          const badge = result ? RESULT_BADGE[result] : { label: 'Não iniciado', variant: 'neutral' as const };
          return (
            <div key={item.id} className="card">
              <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ flex: 1 }}>
                  <div className="muted" style={{ fontSize: 13 }}>Item {item.number}</div>
                  <div style={{ fontWeight: 600, marginTop: 2 }}>{item.competencia}</div>
                  {item.descricao && (
                    <div className="muted" style={{ fontSize: 13, marginTop: 6 }}>{item.descricao}</div>
                  )}
                </div>
                <Badge variant={badge.variant}>{badge.label}</Badge>
              </div>

              <div className="row" style={{ gap: 8, marginTop: 12 }}>
                {SCORE_META.map((s) => {
                  const selected = result === s.key;
                  return (
                    <button
                      key={s.key}
                      className="btn sm"
                      onClick={() => handleScore(item.id, s.key, result)}
                      disabled={!editable}
                      style={{
                        flex: 1,
                        border: `1.5px solid ${selected ? s.color : 'var(--border)'}`,
                        background: selected ? s.color : 'transparent',
                        color: selected ? '#fff' : s.color,
                        fontWeight: 600,
                      }}
                    >
                      {s.label}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {items.length > 0 && (
        <div className="card" style={{ marginTop: 16 }}>
          <div className="field" style={{ marginBottom: 0 }}>
            <label htmlFor="domainSynthesis">Interpretação clínica do domínio</label>
            <textarea
              id="domainSynthesis"
              rows={4}
              value={synthesis}
              onChange={(e) => setSynthesis(e.target.value)}
              onBlur={handleSaveSynthesis}
              disabled={!editable}
              placeholder="Ex.: a comunicação receptiva apresenta-se como uma área de bom desempenho..."
            />
          </div>
        </div>
      )}
    </div>
  );
}
